import React, { useState, useEffect, useRef } from 'react';
import './Menu.css';
import useGenderImages from './hooks/useGenderImages';
import { getBeltImage } from './utils/beltHelper';
import Shop from './Shop';
import CoinShop from './components/shop/CoinShop';
import Statistics from './Statistics';
import Ranking from './Ranking';
import WoodBreaking from './WoodBreaking';
import WoodBreakingChallenge from './WoodBreakingChallenge';
import Friends from './Friends';
import PalmetaGame from './PalmetaGame';
import MartialTournament from './MartialTournament';

import Tutorial from './Tutorial';
import UserBadges from './components/UserBadges';
import './components/UserBadges.css';


function Menu({ userData, onStartTraining, onStartCombatIA, onStartChallenge, onStartMartialTournament, onLogout }) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [avatar, setAvatar] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showShop, setShowShop] = useState(false);
  const [showCoinShop, setShowCoinShop] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showRanking, setShowRanking] = useState(false);
  const [showWoodBreaking, setShowWoodBreaking] = useState(false);
  const [showWoodChallenge, setShowWoodChallenge] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [showPalmetas, setShowPalmetas] = useState(false);
  const [showMartialTournament, setShowMartialTournament] = useState(false);
  const [showAvatarInfo, setShowAvatarInfo] = useState(false);
  const [showAvatarError, setShowAvatarError] = useState(false);
  const [showEnergyWarning, setShowEnergyWarning] = useState(false);
  const [energyWarningMessage, setEnergyWarningMessage] = useState('');

  const audioRef = useRef(null);
  const getImage = useGenderImages(profile?.genero);

  const fetchProfile = async () => {
    const now = Date.now();
    const lastFetch = parseInt(sessionStorage.getItem('lastProfileFetch') || '0');
    
    // Solo hacer petición si han pasado más de 3 segundos
    if (now - lastFetch < 3000) {
      const cached = sessionStorage.getItem('cachedProfile');
      if (cached) {
        const data = JSON.parse(cached);
        setProfile(data);
        if (data.avatar) {
          setAvatar(data.avatar);
          localStorage.setItem('userAvatar', data.avatar);
        }
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Profile data:', data);
        setProfile(data);
        sessionStorage.setItem('cachedProfile', JSON.stringify(data));
        sessionStorage.setItem('lastProfileFetch', now.toString());
        if (data.avatar) {
          setAvatar(data.avatar);
          localStorage.setItem('userAvatar', data.avatar);
        }
      }
    } catch (error) {
      console.error('Error al obtener perfil:', error);
    }
  };

  const playHoverSound = () => {
    const sound = document.getElementById('hover-sound');
    if (sound) {
      sound.currentTime = 0;
      sound.volume = 0.3;
      sound.play().catch(e => console.log('Audio blocked'));
    }
  };

  const handleStartTraining = async () => {
    if (!profile || profile.energia < 10) {
      setEnergyWarningMessage('Necesitas 10 de energía para entrenar.');
      setShowEnergyWarning(true);
      return;
    }
    onStartTraining();
  };

  const handleStartCombat = async () => {
    if (!profile || profile.energia < 15) {
      setEnergyWarningMessage('Necesitas 15 de energía para combatir.');
      setShowEnergyWarning(true);
      return;
    }
    onStartCombatIA();
  };

  const handleStartRotura = async () => {
    if (!profile || profile.energia < 5) {
      setEnergyWarningMessage('Necesitas 5 de energía para rotura.');
      setShowEnergyWarning(true);
      return;
    }
    setShowWoodBreaking(true);
  };

  const handleStartWoodChallenge = async () => {
    if (!profile || profile.energia < 5) {
      setEnergyWarningMessage('Necesitas 5 de energía para el desafío.');
      setShowEnergyWarning(true);
      return;
    }
    setShowWoodChallenge(true);
  };

  const handleStartPalmetas = async () => {
    if (!profile || profile.energia < 5) {
      setEnergyWarningMessage('Necesitas 5 de energía para palmetas.');
      setShowEnergyWarning(true);
      return;
    }
    setShowPalmetas(true);
  };

  const handleShowTutorial = () => {
    const tutorial = new Tutorial();
    tutorial.show();
  };

  const menuCards = [
    { id: 9, img: getImage('saludo'), title: 'TUTORIAL', desc: 'Cómo jugar', action: handleShowTutorial },
    { id: 1, img: getImage('entrenamiento'), title: 'ENTRENAMIENTO', desc: 'Plan personalizado', action: handleStartTraining },
    { id: 2, img: getImage('combateia'), title: 'COMBATE IA', desc: 'Compite online', action: handleStartCombat },
    { id: 3, img: getImage('madera'), title: 'ROTURA', desc: 'Rompe maderas', action: handleStartRotura },
    { id: 10, img: getImage('maderapotencia'), title: 'GOLPE PERFECTO', desc: 'Timing y potencia', action: handleStartWoodChallenge },
    { id: 11, img: getImage('palmetas'), title: 'PALMETAS', desc: 'Reflejos y esquiva', action: handleStartPalmetas },
    { id: 12, img: '/assets/menu/torneomartial.png', title: 'TORNEO MARCIAL', desc: '11 estilos', action: () => { if (onStartMartialTournament) onStartMartialTournament(); else setShowMartialTournament(true); } },
    { id: 4, img: getImage('estadisticas'), title: 'ESTADÍSTICAS', desc: 'Tu progreso', action: () => setShowStats(true) },
    { id: 5, img: getImage('ranking'), title: 'RANKING', desc: 'Clasificación', action: () => setShowRanking(true) },
    { id: 6, img: getImage('tienda'), title: 'TIENDA', desc: 'Equipamiento', action: () => setShowShop(true) },
    { id: 7, img: getImage('monedas'), title: 'MONEDAS', desc: 'Comprar', action: () => setShowCoinShop(true) },
    { id: 8, img: getImage('amigos'), title: 'AMIGOS', desc: 'Social', action: () => setShowFriends(true) }
  ];

  const carouselNext = () => {
    setCarouselIndex(prev => Math.min(prev + 1, menuCards.length - 1));
  };

  const carouselPrev = () => {
    setCarouselIndex(prev => Math.max(prev - 1, 0));
  };

  // Responsive card gap
  const getCardGap = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isLandscape = w > h && h < 500;
    if (isLandscape) return 220;
    if (w <= 480) return 240;
    if (w <= 768) return 260;
    return 320;
  };
  const [cardGap, setCardGap] = useState(getCardGap);

  useEffect(() => {
    const onResize = () => setCardGap(getCardGap());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Touch swipe
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? carouselNext() : carouselPrev(); }
    touchStart.current = null;
  };

  const uploadAvatar = async () => {
    setShowAvatarInfo(true);
  };

  const handleAvatarUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        // Validar tamaño (500KB)
        if (file.size > 500 * 1024) {
          setShowAvatarInfo(false);
          setTimeout(() => {
            setShowAvatarError(true);
          }, 100);
          return;
        }
        
        const reader = new FileReader();
        reader.onload = async (event) => {
          const avatarData = event.target.result;
          setShowAvatarInfo(false);
          
          // Guardar en la base de datos
          try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/avatar`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ avatar: avatarData })
            });
            
            if (response.ok) {
              setAvatar(avatarData);
              localStorage.setItem('userAvatar', avatarData);
              sessionStorage.removeItem('lastProfileFetch');
              sessionStorage.removeItem('cachedProfile');
              await fetchProfile();
            } else {
              console.error('Error al guardar avatar');
              setShowAvatarError(true);
            }
          } catch (error) {
            console.error('Error al guardar avatar:', error);
            setShowAvatarError(true);
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  useEffect(() => {
    const savedAvatar = localStorage.getItem('userAvatar');
    if (savedAvatar) setAvatar(savedAvatar);
    
    fetchProfile();
    
    // Iniciar música del menú
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(err => console.log('Error al reproducir audio:', err));
    }
    
    // Recargar perfil cuando se actualiza desde Shop
    const handleProfileUpdate = () => {
      sessionStorage.removeItem('lastProfileFetch'); // Invalidar caché
      fetchProfile();
    };
    window.addEventListener('profileUpdate', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profileUpdate', handleProfileUpdate);
      // Detener música al desmontar
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      if (showWoodBreaking || showWoodChallenge || showPalmetas || showMartialTournament) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(err => console.log('Error al reproducir audio:', err));
        sessionStorage.removeItem('lastProfileFetch');
        sessionStorage.removeItem('cachedProfile');
        fetchProfile();
      }
    }
  }, [showWoodBreaking, showWoodChallenge, showPalmetas, showMartialTournament]);

  // Recargar perfil cuando se cierra cualquier modal
  useEffect(() => {
    if (!showShop && !showStats && !showRanking && !showFriends && !showWoodBreaking && !showWoodChallenge && !showPalmetas) {
      sessionStorage.removeItem('lastProfileFetch');
      sessionStorage.removeItem('cachedProfile');
      fetchProfile();
    }
  }, [showShop, showStats, showRanking, showFriends, showWoodBreaking, showWoodChallenge, showPalmetas]);

  return (
    <>
    <div className="menu-container">
      <audio ref={audioRef} src="/audio/taekwondomaster.mp3" loop />
      {[...Array(30)].map((_, i) => (
        <div key={i} className="ash-particle"></div>
      ))}

      <div className="menu-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <img src="/img/logo.png" alt="Logo" className="logo-icon" />
          <a href="https://instagram.com/taekwondomaster_thegame" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: '#fff', fontSize: '16px', fontWeight: 'bold' }}>
            <span>Seguinos en Instagram</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
        </div>
        
        <div className="user-info">
          <div className="user-avatar" onClick={uploadAvatar}>
            {avatar ? (
              <img src={avatar} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
            ) : (
              <span>📷</span>
            )}
          </div>
          
          <div className="user-stats">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ margin: 0 }}>{profile?.nombre || userData?.nombre || 'Guerrero'}</h3>
              {profile?.pais && (
                <img 
                  src={`https://flagcdn.com/20x15/${profile.pais.toLowerCase()}.png`}
                  alt={profile.pais}
                  width="20"
                  height="15"
                  onError={(e) => { console.log('Error cargando bandera:', profile.pais); e.target.style.display = 'none'; }}
                />
              )}
            </div>
            <p>
              {profile?.cinturon || 'Cinturón Blanco'} • 
              {getBeltImage(profile?.cinturon) ? (
                <img 
                  src={getBeltImage(profile?.cinturon)} 
                  alt={`Nivel ${profile?.nivel || 1}`} 
                  className="belt-image-menu"
                  title={`Nivel ${profile?.nivel || 1}`}
                />
              ) : (
                <>Nivel {profile?.nivel || 1}</>
              )}
            </p>
            {profile && <UserBadges user={profile} size="small" />}
            
            <div className="xp-container">
              <div className="xp-info">
                <span className="xp-label">⚡ XP</span>
                <span className="xp-numbers">{(profile?.xp || 0) % 100} / 100</span>
              </div>
              <div className="xp-bar">
                <div className="xp-fill" style={{ width: `${((profile?.xp || 0) % 100)}%` }}></div>
              </div>
            </div>
            
            <div className="user-attributes">
              <span className="attr" title="Fuerza">💪 {profile?.fuerza_total || 10}</span>
              <span className="attr" title="Defensa">🛡️ {profile?.defensa_total || 0}</span>
              <span className="attr" title="Velocidad">⚡ {profile?.velocidad_total || 10}</span>
              <span className="attr" title="Precisión">🎯 {profile?.tecnica_total || 10}</span>
              <span className="attr" title="Energía">🔋 {profile?.energia || 100}</span>
              <span className="attr" title="Monedas">💰 {profile?.monedas || 0}</span>
            </div>
          </div>
          
          <div className="menu-card logout-card" onClick={onLogout}>
            <span className="logout-icon">🚪</span>
            <span className="logout-text">SALIR</span>
          </div>
        </div>
      </div>

      <div className="menu-grid" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <button className="carousel-btn prev" onClick={carouselPrev} disabled={carouselIndex === 0}>‹</button>
        <button className="carousel-btn next" onClick={carouselNext} disabled={carouselIndex === menuCards.length - 1}>›</button>
        
        <div className="menu-carousel">
          {menuCards.map((card, index) => {
            const offset = index - carouselIndex;
            return (
              <div 
                key={card.id} 
                className={`menu-card ${offset === 0 ? 'active' : ''}`}
                style={{
                  transform: `translateX(${offset * cardGap}px) scale(${offset === 0 ? 1 : Math.max(0.65, 0.85 - Math.abs(offset) * 0.1)}) rotateY(${offset * -5}deg)`,
                  opacity: offset === 0 ? 1 : Math.max(0.3, 0.7 - Math.abs(offset) * 0.15),
                  filter: offset === 0 ? 'none' : `grayscale(${Math.min(80, Math.abs(offset) * 30)}%)`,
                  zIndex: 10 - Math.abs(offset),
                  pointerEvents: offset === 0 ? 'auto' : 'none',
                }}
                onMouseEnter={offset === 0 ? playHoverSound : undefined}
                onClick={offset === 0 ? card.action : undefined}
              >
                <div className="card-icon">
                  <img src={card.img} alt={card.title} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    {showShop && <Shop onClose={() => setShowShop(false)} />}
    {showCoinShop && (
      <CoinShop 
        userCoins={profile?.monedas || 0}
        onClose={() => setShowCoinShop(false)}
        onPurchaseSuccess={(coins) => {
          sessionStorage.removeItem('lastProfileFetch');
          fetchProfile();
        }}
      />
    )}
    {showStats && <Statistics onClose={() => setShowStats(false)} />}
    {showRanking && <Ranking onClose={() => setShowRanking(false)} />}
    {showWoodBreaking && <WoodBreaking onClose={() => setShowWoodBreaking(false)} userData={profile} />}
    {showWoodChallenge && <WoodBreakingChallenge onClose={() => setShowWoodChallenge(false)} userData={profile} />}
    {showPalmetas && <PalmetaGame onClose={() => setShowPalmetas(false)} userData={profile} />}
    {showMartialTournament && <MartialTournament userData={profile} onStartFight={(index) => { setShowMartialTournament(false); if (window._startMartialFight) window._startMartialFight(index); }} onBack={() => setShowMartialTournament(false)} />}
    {showFriends && <Friends onClose={() => setShowFriends(false)} onStartChallenge={(challengeId, oponenteIa) => { setShowFriends(false); if (onStartChallenge) onStartChallenge(challengeId, oponenteIa); }} />}
    
    {showEnergyWarning && (
      <div className="overlay" onClick={() => setShowEnergyWarning(false)}>
        <div className="energy-warning-modal" onClick={(e) => e.stopPropagation()}>
          <div className="warning-icon">⚠️</div>
          <h2>Energía Insuficiente</h2>
          <p>{energyWarningMessage}</p>
          <p className="energy-info">🔋 La energía se renueva cada día</p>
          <p className="energy-info">🛍️ Puedes comprar más en la tienda</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button className="warning-btn" onClick={() => setShowEnergyWarning(false)}>ENTENDIDO</button>
            <button className="warning-btn shop-btn" onClick={() => { setShowEnergyWarning(false); setShowShop(true); }}>IR A TIENDA</button>
          </div>
        </div>
      </div>
    )}

    {showAvatarInfo && (
      <div className="overlay" onClick={() => setShowAvatarInfo(false)}>
        <div className="energy-warning-modal" onClick={(e) => e.stopPropagation()}>
          <div className="warning-icon">📷</div>
          <h2>Subir Avatar</h2>
          <p style={{ marginBottom: '15px' }}>Especificaciones de la imagen:</p>
          <div style={{ textAlign: 'left', marginBottom: '20px' }}>
            <p className="energy-info">📏 Tamaño recomendado: 500x500 px</p>
            <p className="energy-info">💾 Peso máximo: 500 KB</p>
            <p className="energy-info">📁 Formatos: JPG, PNG, WebP</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
            <button className="warning-btn" onClick={() => setShowAvatarInfo(false)}>CANCELAR</button>
            <button className="warning-btn shop-btn" onClick={handleAvatarUpload}>SUBIR</button>
          </div>
        </div>
      </div>
    )}

    {showAvatarError && (
      <div className="overlay" onClick={() => setShowAvatarError(false)}>
        <div className="energy-warning-modal" onClick={(e) => e.stopPropagation()}>
          <div className="warning-icon">⚠️</div>
          <h2>Imagen muy grande</h2>
          <p>La imagen no puede pesar más de 500 KB</p>
          <p className="energy-info">Por favor, selecciona una imagen más pequeña</p>
          <button className="warning-btn" onClick={() => setShowAvatarError(false)} style={{ marginTop: '20px' }}>ENTENDIDO</button>
        </div>
      </div>
    )}
    </>
  );
}

export default Menu;
